import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import RequiredFieldDisclaimer from '../components/RequiredFieldDisclaimer';
import DatePicker, { convertDateToIso8601String } from '../components/DatePicker';
import TextCollectionField from './TextCollectionField';
import RadioCollectionField from './RadioCollectionField';
import SiboCollectionFields, {possibleEightTubeSiboPack} from './SiboCollectionFields';
import DropShipInstructionsForm from './DropShipInstructionsForm';
import RouteNumberInput from '../components/RouteNumberInput';
import {getPreviousPatientInstructions, saveSampleCollection} from '../util/apiUtils';
import TestPrepInfo from './TestPrepInfo';
import CollectionTimeFields from './CollectionTimeForm';
import AlertMessage from '../AlertMessage';
import EndoPlusCollectionSummary, {hasEndoPlusCustomInstructions} from './EndoPlusCollection';


// The list of input fields (by accessionKey) that are handled by this component
const validProfileSpecificFieldAccessionKeys = [
	'Total Urine Volume',
	'# Hours (Collection Duration)',
	'Sample Hours',
	'Sample Color',
	'Sample Consistency',
	'Tube 1',
	'Tube 2',
	'Tube 3',
	'Tube 4',
	'Tube 5',
	'Tube 6',
	'Tube 7',
	'Tube 8',
	'Tube 9',
	'Drank Solution',
	'Specimen Collection-1day',
	'Specimen Collection-3day',
	'Specimen Consistency-hard',
	'Specimen Consistency-normal',
	'Specimen Consistency-loose',
	'Specimen Consistency-watery',
	'Specimen Color-brown',
	'Specimen Color-green',
	'Specimen Color-yellow',
	'Specimen Color-black',
	'Collection Time #1',
	'Collection Time #2',
	'Collection Time #3',
	'Collection Time #4',
	'Collection Time #5'
];

const SampleCollectionForm = forwardRef(({ gearRequisition, omsRequisition, gearOrder, userType }, ref) => {
	const [form, setForm] = useState({});
	const [previousInstructions, setPreviousInstructions] = useState([]);
	const dropShipRef = useRef();
	const orderableItem = omsRequisition?.orderableItem;
	const orderableItemId = orderableItem?.id;
	const gsdlNo = gearOrder?.practitioner?.gsdlNo;

	// Should we disable after acessioning? What about using this for STARS resolution?
	// const [disableInputs, ] = useState(!!gearRequisition?.accessionNo);
	const disableInputs = false;

	useEffect(() => {
		setForm(buildInitialForm(gearRequisition, orderableItem));
	}, [gearRequisition, orderableItem]);
	useEffect(() => {
		loadPreviousInstructions(gsdlNo, orderableItemId);
	}, [gsdlNo, orderableItemId]);

	/**
	 * Send a request to the back-end to save the current values.
	 * If we're displaying the dropShipInstructions form, let it handle its own save.
	 */
	const saveCollectionInfo = useCallback(() => {
		if (isShowDropShipInstructions(gearRequisition, userType) && dropShipRef?.current?.save) {
			return dropShipRef.current.save();
		}

		const validatedForm = validateForm(form, gearRequisition);
		setForm(validatedForm);

		if (Object.values(validatedForm).every((prop) => prop.valid)) {
			const requisition = { ...gearRequisition };
			let sampleCollectionData = [];
			if (validatedForm.isLastSampleCollected.value === 'true') {
				for (const field of Object.values(form)) {
					if (field?.id) {
						sampleCollectionData.push({
							id: field.id,
							value: field.value
						});
					}
				}
			}

			requisition.sampleCollectionData = sampleCollectionData;
			const dateLastSampleCollected =
				(form.isLastSampleCollected?.value === 'true' && form.dateLastSampleCollected?.value) || '';
			requisition.dateLastSampleCollected = dateLastSampleCollected;
			requisition.routeNumber = validatedForm.routeNumber.value ?? '';
			requisition.patientInstructions = form?.patientInstructions?.value ?? '';

			return saveSampleCollection(requisition);
		} else {
			return Promise.reject('ValidationError');
		}
	}, [form, gearRequisition, userType]);

	/**
	 * Determines if the form has any unsaved changes.
	 * If we're displaying the dropShipInstructions form, let it handle its own check.
	 */
	const isDirty = useCallback(() => {
		if (isShowDropShipInstructions(gearRequisition, userType) && dropShipRef?.current?.isDirty) {
			return dropShipRef.current.isDirty();
		}

		const initialForm = buildInitialForm(gearRequisition, orderableItem);
		if (form === initialForm || !form || !initialForm) {
			return false;
		}

		for (const [accessionKey, input] of Object.entries(form)) {
			let currentValue = getBooleanOrString(input?.value);
			let initialValue = getBooleanOrString(initialForm[accessionKey]?.value);

			if (currentValue !== initialValue) {
				return true;
			}
		}

		if (form.patientInstructions?.value !== initialForm.patientInstructions?.value) {
			return true;
		}
		
		if (form.routeNumber?.value !== initialForm.routeNumber?.value) {
			return true;
		}

		return false;
	}, [form, gearRequisition, orderableItem, userType]);

	/**
	 * Reset the form to the original values.
	 * If we're displaying the dropShipInstructions form, let it handle its own reset.
	 */
	const reset = useCallback(() => {
		if (isShowDropShipInstructions(gearRequisition, userType) && dropShipRef?.current?.reset) {
			dropShipRef.current.reset();
			return;
		}
		setForm(buildInitialForm(gearRequisition, orderableItem));
	}, [gearRequisition, orderableItem, userType]);

	/**
	 * This method builds the ref properties that will be available to the parent. We are exposing
	 * three functions, 'save', 'reset', and 'isDirty'.
	 */
	useImperativeHandle(
		ref,
		() => {
			return {
				save: saveCollectionInfo,
				reset,
				isDirty
			};
		},
		[isDirty, reset, saveCollectionInfo]
	);

	/**
	 * Clear all validation errors
	 */
	const resetFormValidation = () => {
		if (form) {
			setForm((prevForm) => {
				let updatedForm = { ...prevForm };
				Object.values(updatedForm).forEach((prop) => {
					prop.valid = null;
					prop.message = '';
				});
				return updatedForm;
			})
		}
	}

	/**
	 * Update the form with the changed value.
	 * @param event The HTML event that triggered this function. The event is expected to have a 'data-accessionkey' attribute.
	 */
	const handleProfileSpecificFieldChange = useCallback((event) => {
		event.stopPropagation();

		const accessionKey = event.target.dataset.accessionkey;
		const value = event.target.value;

		setForm((prevForm) => {
			return {
				...prevForm,
				[accessionKey]: {
					value: value,
					id: prevForm[accessionKey]?.id,
					accessionKey: accessionKey
				}
			};
		});
	}, []);

	/**
	 * Update the form when a radio input changes.
	 * Each radio input is expected to have a 'data-relatedaccessionkeys' attribute containing the list of fields to toggle off.
	 */
	const handleRadioFieldChange = (event) => {
		event.stopPropagation();
		const accessionKey = event.target.dataset.accessionkey;
		const relatedAccessionKeys = event.target.dataset.relatedaccessionkeys?.split('|');

		let newForm = { ...form };
		form[accessionKey].value = event.target.checked;
		form[accessionKey].valid = true;

		if (relatedAccessionKeys) {
			relatedAccessionKeys.forEach((key) => {
				if (form[key]) {
					form[key].value = false;
					form[key].valid = true;
				}
			});
		}

		setForm(newForm);
	};

	const handleSampleDateChange = (dateStr) => {
		setForm((prevForm) => {
			return {
				...prevForm,
				dateLastSampleCollected: {
					value: dateStr
				}
			};
		});
	};

	/**
	 * Update the state's 'form' object with the new routeNumber value.
	 */
	const handleRouteNumberChange = (event) => {
		const value = event.target?.value ?? '';
		setForm((prevForm) => {
			return {
				...prevForm,
				routeNumber: {
					value: value
				}
			};
		});
	}

	/**
	 * Update the state's 'form' object with the new patientInstructions value.
	 */
	const handlePatientInstructionsChange = (event) => {
		const value = event.target?.value ?? '';
		setForm((prevForm) => {
			return {
				...prevForm,
				patientInstructions: {
					value: value
				}
			};
		});
	}

	/**
	 * Set the patient instructions, overwriting the current instructions.
	 */
	const setPatientInstructions = (value) => {
		setForm((prevForm) => {
			return {
				...prevForm,
				patientInstructions: {
					value: value
				}
			};
		});
	}

	/**
	 * Fetches a list of patient instructions recently saved by the current order's pracitioner.
	 */
	const loadPreviousInstructions = (gsdlNo, orderableItemId) => {
		if (gsdlNo && orderableItemId) {
			getPreviousPatientInstructions(gsdlNo, orderableItemId)
			.then((previousInstructions) => {
				setPreviousInstructions(previousInstructions);
			})
			.catch((error) => {
				// Ignore errors. No previous instructions will be displayed
			});
		}
	};

	// Max collection date is +1 month for Covid or +7 days for anything else
	let maxCollecitonDate = new Date();
	if (gearRequisition.covidRegistration) {
		maxCollecitonDate.setMonth(maxCollecitonDate.getMonth() + 1);
	} else {
		maxCollecitonDate.setDate(maxCollecitonDate.getDate() + 7);
	}

	// Min collection date is -14 days in the past
	let minCollectionDate = new Date();
	minCollectionDate.setDate(minCollectionDate.getDate() - 14);

	// Only use inline date picker if the collection date has not been previously saved.
	let showInlineDateSelection = !gearRequisition?.dateLastSampleCollected;

	// prefix the ids to prevent duplicate ids when multiple forms are displayed on the same page (Confirmation page)
	const idPrefix = gearRequisition?.id;

	const customInstructions = hasEndoPlusCustomInstructions(omsRequisition?.orderableItem, gearRequisition);

	// Display the drop-ship instructions component instead of sample collection when appropriate
	if (isShowDropShipInstructions(gearRequisition, userType)) {
		return (
			<DropShipInstructionsForm
				ref={dropShipRef}
				gsdlNo={gsdlNo}
				orderableItem={omsRequisition?.orderableItem}
				gearRequisition={gearRequisition}
				gearOrder={gearOrder}
			/>
		);
	}

	return (
		<div>
			{(userType !== 'myGDX' && (form?.patientInstructions?.value || customInstructions)) && (
				<AlertMessage type='info'>
					{(form?.patientInstructions?.value) && (
						<>
							<h4 className="mb-0">Important notes from your physician</h4>
							<p>{form.patientInstructions.value}</p>
						</>
					)}
					{(customInstructions) && (
						<>
							{(form?.patientInstructions?.value) && <div className="mt-20"></div>}
							<h4 className="mb-0">Collection Instructions</h4>
							<div><EndoPlusCollectionSummary gearRequisition={gearRequisition} orderableItem={omsRequisition?.orderableItem} includeInstructions={true} /></div>
						</>
					)}
				</AlertMessage>
			)}
			{(userType === 'myGDX') && (
				<div>
					<label htmlFor="patient-instructions">
						Patient Instructions / Order Notes <small className="text-muted">(Will be shown to patients when they activate online)</small>
					</label>
					{!!previousInstructions?.length && (
							<div>
								{previousInstructions.map((instruction, index) => {
									return (
											<div
													key={index}
													className="previous-patient-instructions"
													onClick={() => setPatientInstructions(instruction)}
											>
												{instruction}
											</div>
									);
								})}
							</div>
					)}
					<textarea
						id="patient-instructions"
						className="form-control"
						maxLength="512"
						rows="3"
						value={form?.patientInstructions?.value ?? ''}
						onChange={handlePatientInstructionsChange}
					></textarea>
					<span className="pull-right">
						{form?.patientInstructions?.value?.length || 0}/{512} characters
					</span>
					<div className="clearfix"></div>
				</div>
			)}
			<div id="sampleCollectedSection">
				<div className={form.isLastSampleCollected?.valid === false ? 'has-error' : ''}>
					<h4>
						{!gearRequisition.covidRegistration
							? 'Has the sample already been collected, or is it scheduled to be collected in the near future?'
							: 'Is there a scheduled date for sample collection?'}
						<span className="text-danger">*</span>
					</h4>
					&nbsp;&nbsp;
					<input
						id={`${idPrefix}_isLastSampleCollectedYes`}
						name={`${idPrefix}_isLastSampleCollected`}
						type="radio"
						value="true"
						data-fieldid={'isLastSampleCollected'}
						data-accessionkey={'isLastSampleCollected'}
						checked={form.isLastSampleCollected?.value === 'true'}
						onChange={handleProfileSpecificFieldChange}
					/>
					<label className="control-label" htmlFor={`${idPrefix}_isLastSampleCollectedYes`}>
						Yes
					</label>
					&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
					<input
						id={`${idPrefix}_isLastSampleCollectedNo`}
						name={`${idPrefix}_isLastSampleCollected`}
						type="radio"
						value="false"
						data-fieldid={'isLastSampleCollected'}
						data-accessionkey={'isLastSampleCollected'}
						checked={form.isLastSampleCollected?.value === 'false'}
						onChange={handleProfileSpecificFieldChange}
					/>
					<label className="control-label" htmlFor={`${idPrefix}_isLastSampleCollectedNo`}>
						No
					</label>
					{form.isLastSampleCollected?.valid === false && (
						<div className="validation-message text-danger">{form.isLastSampleCollected.message}</div>
					)}
					{form.isLastSampleCollected?.value === 'true' && (
						<div className={'space-bottom' + (form.dateLastSampleCollected?.valid === false ? ' has-error' : '')}>
							<label className="control-label" htmlFor="dateLastSampleCollected">
								Date Last Sample Collected<span className="text-danger">*</span>
							</label>
							<br />
							<DatePicker
								inline={showInlineDateSelection}
								showMonthDropdown={false}
								showYearDropdown={false}
								dateStr={form?.dateLastSampleCollected.value}
								onChange={handleSampleDateChange}
								maxDate={maxCollecitonDate}
								minDate={minCollectionDate}
								title="Date Last Sample Collected is required"
								popperPlacement="top-end"
							/>
							{form?.dateLastSampleCollected.valid === false && (
								<span className="validation-message text-danger" style={{ display: 'block' }}>
									{form?.dateLastSampleCollected.message}
								</span>
							)}
						</div>
					)}
				</div>
			</div>
			{form.isLastSampleCollected?.value === 'false' && (
				<div className="alert alert-info" role="alert">
					Please return to this page to document the date of final sample collection when it has been completed.
				</div>
			)}
			{form.isLastSampleCollected?.value === 'true' && (
				<div className="row">
					<TextCollectionField
						id={`${idPrefix}_numHours`}
						labelText={
							<>
								Collection Duration
								<br className="visible-md-block" />
								<em>
									<small> (# of Hours)</small>
								</em>
							</>
						}
						field={form['# Hours (Collection Duration)']}
						onChange={handleProfileSpecificFieldChange}
						className="col-md-3"
					/>
					<TextCollectionField
						id={`${idPrefix}_tuv`}
						labelText={
							<>
								Total Urine Volume
								<br className="visible-md-block" />
								<em>
									<small> (ml)</small>
								</em>
							</>
						}
						field={form['Total Urine Volume']}
						onChange={handleProfileSpecificFieldChange}
						className="col-md-3"
					/>
					<TextCollectionField
						id={`${idPrefix}_sampleHours`}
						labelText="Total Collection Time"
						field={form['Sample Hours']}
						onChange={handleProfileSpecificFieldChange}
						className="col-md-3"
					/>
					{form['Sample Color'] && (
						<>
							<div className={'col-md-6' + (form['Sample Color'].valid === false ? ' has-error' : '')}>
								<label className="control-label" htmlFor={`${idPrefix}_sampleColor`}>
									Sample Color<span className="text-danger">*</span>
								</label>
								<select
									className="form-control"
									id={`${idPrefix}_sampleColor`}
									type="text"
									onChange={handleProfileSpecificFieldChange}
									value={form['Sample Color'].value}
									data-accessionkey={form['Sample Color'].accessionKey}
								>
									<option />
									<option value="Green">Green</option>
									<option value="Light Brown">Light Brown</option>
									<option value="Brown">Brown</option>
									<option value="Black">Black</option>
								</select>
								{form['Sample Color'].valid === false && (
									<span className="validation-message text-danger">Please fill out this field.</span>
								)}
							</div>
							{form['Sample Consistency'] && (
								<div
									className={
										'col-md-6' + (form['Sample Consistency'].valid === false ? ' has-error' : '')
									}
								>
									<label className="control-label" htmlFor={`${idPrefix}_sampleConsistency`}>
										Sample Consistency<span className="text-danger">*</span>
									</label>
									<select
										className="form-control"
										id={`${idPrefix}_sampleConsistency`}
										type="text"
										onChange={handleProfileSpecificFieldChange}
										value={form['Sample Consistency'].value}
										data-accessionkey={form['Sample Consistency'].accessionKey}
									>
										<option />
										<option value="Hard/Constip.">Hard/Constipated</option>
										<option value="Formed/Normal">Formed/Normal</option>
										<option value="Loose">Loose</option>
										<option value="Watery/Diarrhea">Watery/Diarrhea</option>
									</select>
									{form['Sample Consistency'].valid === false && (
										<span className="validation-message text-danger">
											Please fill out this field.
										</span>
									)}
								</div>
							)}
						</>
					)}
					<RadioCollectionField
						labelText="Specimen Collection:"
						collectionName="collectionTime"
						disabled={disableInputs}
						onChange={handleRadioFieldChange}
						form={form}
						items={[
							{
								id: `${idPrefix}_1day`,
								label: '1 Day',
								accessionKey: 'Specimen Collection-1day'
							},
							{
								id: `${idPrefix}_3day`,
								label: '3 Day',
								accessionKey: 'Specimen Collection-3day'
							}
						]}
					/>

					<RadioCollectionField
						labelText="Specimen Consistency: "
						collectionName="collectionConsistency"
						disabled={disableInputs}
						onChange={handleRadioFieldChange}
						form={form}
						items={[
							{
								id: `${idPrefix}_hard`,
								label: 'Hard',
								accessionKey: 'Specimen Consistency-hard'
							},
							{
								id: `${idPrefix}_formed/normal`,
								label: 'Formed/Normal',
								accessionKey: 'Specimen Consistency-normal'
							},
							{
								id: `${idPrefix}_loose`,
								label: 'Loose',
								accessionKey: 'Specimen Consistency-loose'
							},
							{
								id: `${idPrefix}_watery/diarrhea`,
								label: 'Watery/Diarrhea',
								accessionKey: 'Specimen Consistency-watery'
							}
						]}
					/>
					<RadioCollectionField
						labelText="Specimen Color: "
						collectionName="collectionColor"
						disabled={disableInputs}
						onChange={handleRadioFieldChange}
						form={form}
						items={[
							{
								id: `${idPrefix}_brown`,
								label: 'Brown',
								accessionKey: 'Specimen Color-brown'
							},
							{
								id: `${idPrefix}_green`,
								label: 'Green',
								accessionKey: 'Specimen Color-green'
							},
							{
								id: `${idPrefix}_yellow`,
								label: 'Yellow',
								accessionKey: 'Specimen Color-yellow'
							},
							{
								id: `${idPrefix}_black`,
								label: 'Black',
								accessionKey: 'Specimen Color-black'
							}
						]}
					/>
					<SiboCollectionFields form={form} onChange={handleProfileSpecificFieldChange} omsRequisitionId={gearRequisition?.reqNumber} resetFormValidation={resetFormValidation} />
					<CollectionTimeFields  form={form} onChange={handleProfileSpecificFieldChange} />
				</div>
			)}
			{(userType === 'myGDX') && (
				<div className="space-top">
					<RouteNumberInput value={form?.routeNumber?.value ?? ''} onChange={handleRouteNumberChange}/>
				</div>
			)}
			<div className="space-top">
				<RequiredFieldDisclaimer />
			</div>

			<TestPrepInfo userType={userType} />
		</div>
	);
});

const buildInitialForm = (gearRequisition, orderableItem) => {
	let newForm = {
		isLastSampleCollected: {
			value: gearRequisition?.dateLastSampleCollected ? 'true' : null
		},
		dateLastSampleCollected: {
			value: gearRequisition?.dateLastSampleCollected || convertDateToIso8601String(new Date())
		},
		routeNumber: {
			value: gearRequisition?.routeNumber ?? ''
		},
		patientInstructions: {
			value: gearRequisition?.patientInstructions ?? ''
		}
	};

	if (orderableItem?.inputFields) {
		orderableItem.inputFields.forEach((field, index) => {
			if (validProfileSpecificFieldAccessionKeys.indexOf(field.accessionKey) !== -1) {
				// do not include collection times 8 and 9 for 2-hr SIBO
				if (
					!(
						gearRequisition.panel === '4710' &&
						(field.accessionKey === 'Tube 8' || field.accessionKey === 'Tube 9')
					)
				) {
					let reqField = gearRequisition?.profileSpecificFields.find((reqField) => reqField.id === field.id);

					/**
					 * SCR-8767: SIBO field names have changed from "Sample x" to "Tube x". Map the old name to new 
					 * name so we will load values saved before the switch.
					 */
					if (
						gearRequisition?.omsOrderableItemId === 215 &&
						field.accessionKey?.startsWith('Tube') &&
						!reqField?.value
					) {
						reqField = gearRequisition?.profileSpecificFields.find(
							(reqField) => reqField.accessionKey === mapSiboTubeNumberToSampleNumber(field.accessionKey)
						);
					}

					let reqValue = reqField?.value ?? '';
					if (reqValue === 'false') {
						reqValue = '';
					} else if (reqValue === 'true') {
						reqValue = true;
					}

					newForm[field.accessionKey] = {
						id: field.id,
						value: reqValue,
						accessionKey: field.accessionKey
					};
				}
			}
		});
	}

	return newForm;
};

/**
 * The numbers after '1' shift down because, with the switch to Tube Numbers, we started capturing
 * the second baseline sample. Samples 1 & 2 used to share a single collection time input.
 */
const mapSiboTubeNumberToSampleNumber = (tubeAccessionKey) => {
	switch (tubeAccessionKey) {
		case 'Tube 1':
			return 'Sample 1';
		case 'Tube 3':
			return 'Sample 2';
		case 'Tube 4':
			return 'Sample 3';
		case 'Tube 5':
			return 'Sample 4';
		case 'Tube 6':
			return 'Sample 5';
		case 'Tube 7':
			return 'Sample 6';
		case 'Tube 8':
			return 'Sample 7';
		case 'Tube 9':
			return 'Sample 8';
		default:
			return null;
	}
}

const validateForm = (form, gearRequisition) => {
	let validatedForm = { ...form };
	Object.values(validatedForm).forEach((prop) => {
		prop.valid = true;
		prop.message = '';
	});
	if (!gearRequisition?.dropShip || gearRequisition?.reqNumber) {
		if (validatedForm.isLastSampleCollected.value === null) {
			validatedForm.isLastSampleCollected.valid = false;
			validatedForm.isLastSampleCollected.message = 'Please indicate whether the last sample has been collected.';
		}
		if (validatedForm.isLastSampleCollected.value === 'true' && !validatedForm.dateLastSampleCollected.value) {
			validatedForm.dateLastSampleCollected.valid = false;
			validatedForm.dateLastSampleCollected.message = 'Please enter the last sample collection date.';
		}

		// if sample collected then validate the profile specific fields
		if (validatedForm.isLastSampleCollected.value === 'true') {
			for (const [accessionKey, field] of Object.entries(validatedForm)) {

				// SCR-8767: Do not require 'Tube 2' for old SIBO reqs
				if (
					gearRequisition?.omsOrderableItemId === 215 &&
					accessionKey === 'Tube 2' &&
					possibleEightTubeSiboPack(gearRequisition?.reqNumber)
				) {
					continue;
				}

				// boolean false means this is a radio button that has been triggered
				if (accessionKey && (validProfileSpecificFieldAccessionKeys.indexOf(accessionKey) !== -1) && !field.value && field.value !== false) {
					validatedForm[accessionKey].valid = false;
					validatedForm[accessionKey].message = 'Please fill out this field.';
				}
			}
		}
	}

	return validatedForm;
};

/**
 * Determines whether to show the drop-ship instructions form or the sample collection form
 */
const isShowDropShipInstructions = (gearRequisition, userType) => {
	return gearRequisition?.dropShip && !gearRequisition?.reqNumber && userType === 'myGDX';
};

function getBooleanOrString(input) {
	if (!input) {
		return false;
	}
	if (input === 'false') {
		return false;
	}
	if (input === 'true') {
		return true;
	}
	return input;
}

export default SampleCollectionForm;
SampleCollectionForm.displayName = 'SampleCollectionForm';
